import {
  Button,
  Card,
  CodeBracketIcon,
  ErrorAlert,
  PaintBrushIcon,
  SparklesIcon,
  success,
  useCanShow,
  useCustomForm,
  useEffect,
  useFetchData,
} from "@";
import { css } from "@codemirror/lang-css";
import CodeMirror from "@uiw/react-codemirror";
import { useThemeColors } from "./utils/hooks";
import ColorGroup from "./components/ColorGroup";
import ColorBox from "./components/ColorBox";
import { getStyles, saveStyles } from "./utils/api";
import { interfaceDefaultValues } from "./utils/validation";

// Predefined palette catalog — gives names + values in one place
const PALETTES = [
  { value: "#41B06E,#141E46,#000000", label: "غابة" },
  { value: "#AD6ED4,#0D0911,#000000", label: "أوركيد" },
  { value: "#DB9C06,#0D0911,#000000", label: "العنبر" },
  { value: "#6C4E31,#0D0911,#000000", label: "كاكاو" },
  { value: "#3282B8,#0F4C75,#1B262C", label: "لازورد" },
  { value: "#395B64,#2C3333,#A5C9CA", label: "نعناع" },
  { value: "#443C68,#393053,#18122B", label: "غسق" },
];

const Interface = () => {
  useCanShow("editAssociationData", "to");

  const { setValue, register, watch, handleSubmit, isSubmitting } =
    useCustomForm(interfaceDefaultValues);

  const { fillForm } = useThemeColors(setValue);

  const { error, fetchData } = useFetchData({
    fill: fillForm,
    fun: getStyles,
  });

  const saveData = async (data) => {
    try {
      await saveStyles(data);
      success("تم الحفظ بنجاح");
    } catch (error) {}
  };

  const customColors = `${watch("colorA")},${watch("colorB")},${watch(
    "colorC",
  )}`;

  useEffect(() => {
    setValue("themeValues", customColors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("colorA"), watch("colorB"), watch("colorC")]);

  const isCustomActive = watch("themeValues") === customColors;

  return (
    <>
      <ErrorAlert reload={fetchData} show={error} />

      <form onSubmit={handleSubmit(saveData)} className="space-y-5">
        {/* Theme picker card */}
        <Card title="ألوان الموقع الإلكتروني">
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <PaintBrushIcon className="h-4 w-4 text-purple-700 dark:text-purple-400" />
            <span>اختر مجموعة الألوان المفضلة لتطبيقها على الموقع</span>
          </div>

          {/* Predefined palettes — responsive grid of theme cards */}
          <div className="lg grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {PALETTES.map((p) => (
              <ColorGroup
                key={p.value}
                input={register("themeValues")}
                checked={watch("themeValues") === p.value}
                value={p.value}
                label={p.label}
              />
            ))}
          </div>

          {/* Custom theme card */}
          <div className="mt-5">
            <div className="mb-2 flex items-center gap-2">
              <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-purple-50 px-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-purple-700 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
                <SparklesIcon className="h-3 w-3" />
                مخصص
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                اصنع لوحتك الخاصة
              </span>
            </div>

            <label
              className={`group/custom block cursor-pointer overflow-hidden rounded-2xl border bg-white p-4 transition-all duration-200 dark:bg-gray-800 ${
                isCustomActive
                  ? "border-purple-300 shadow-sm shadow-purple-900/10 ring-2 ring-purple-200 dark:border-purple-600 dark:ring-purple-700/40"
                  : "border-gray-100 hover:border-purple-200 dark:border-gray-700/60 dark:hover:border-purple-700"
              }`}
            >
              <input
                type="radio"
                className="peer sr-only"
                value={customColors}
                {...register("themeValues")}
                checked={isCustomActive}
                readOnly
              />

              {/* Live preview banner */}
              <div className="relative mb-4 flex h-10 overflow-hidden rounded-2xl ring-1 ring-black/5 dark:ring-white/10">
                <span
                  style={{ backgroundColor: watch("colorA") }}
                  className="flex-1 transition-colors duration-300"
                />
                <span
                  style={{ backgroundColor: watch("colorB") }}
                  className="flex-1 transition-colors duration-300"
                />
                <span
                  style={{ backgroundColor: watch("colorC") }}
                  className="flex-1 transition-colors duration-300"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent"
                />
              </div>

              {/* Color pickers */}
              <div className="flex flex-wrap items-center gap-3">
                {[
                  { key: "colorA", label: "أساسي" },
                  { key: "colorB", label: "ثانوي" },
                  { key: "colorC", label: "تأكيد" },
                ].map(({ key, label }) => (
                  <div
                    key={key}
                    className="flex items-center gap-2 rounded-2xl border border-gray-100 bg-gray-50/40 px-2 py-1.5 dark:border-gray-700/60 dark:bg-gray-900/40"
                  >
                    <span className="text-[11px] font-medium tracking-tight text-gray-500 dark:text-gray-400">
                      {label}
                    </span>
                    <label className="relative h-7 w-7 cursor-pointer overflow-hidden rounded-md ring-1 ring-black/10 dark:ring-white/10">
                      <span
                        className="absolute inset-0"
                        style={{ backgroundColor: watch(key) }}
                      />
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent"
                      />
                      <input
                        type="color"
                        {...register(key)}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      />
                    </label>
                    <span className="font-mono text-[10px] uppercase tabular-nums text-gray-400 dark:text-gray-500">
                      {watch(key)}
                    </span>
                  </div>
                ))}
              </div>
            </label>
          </div>
        </Card>

        {/* Custom CSS editor */}
        <Card isOpen={false} title="تخصيص بال CSS">
          <div className="mb-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <CodeBracketIcon className="h-4 w-4 text-purple-700 dark:text-purple-400" />
            <span>أضف قواعد CSS مخصصة لتطبيقها على واجهة الموقع</span>
          </div>

          <div
            dir="ltr"
            className="customCss overflow-hidden rounded-2xl border border-gray-100 bg-gray-50/30 p-1 dark:border-gray-700/60 dark:bg-gray-900/30"
          >
            <CodeMirror
              value={watch("customCss")}
              className="p-1"
              extensions={[css()]}
              onChange={(value) => setValue("customCss", value)}
              options={{
                theme: "light",
                mode: "css",
                lineNumbers: true,
              }}
            />
          </div>
        </Card>

        {useCanShow("editAssociationData") && (
          <div className="floating-btn">
            <Button
              className="w-full sm:w-auto"
              isLoading={isSubmitting}
              type="submit"
              title="حفظ البيانات"
            />
          </div>
        )}
      </form>
    </>
  );
};

export default Interface;
